import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { RadioTower } from 'lucide-react'
import { Badge, Button, ConfirmDialog, CopyField, EmptyState, Notice, Select, useToast } from '@/components/play'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import { formatAge } from '@/components/map/camera-sphere/formatAge'
import {
  apiErrors,
  connectProvider,
  disconnectSource,
  loadLastDelivery,
  loadProviders,
  loadTrackingSources,
  nextSourceKey,
  rotateWebhook,
} from '@/lib/athletes/api'
import type { TrackingProvider, TrackingSource, WebhookDelivery } from '@/lib/athletes/types'
import { useRefresh } from '@/lib/athletes/useRefresh'
import { workspaceError } from '@/lib/workspace'

/** Past this, a source that has reported is called quiet. */
const QUIET_AFTER_MS = 10 * 60_000

interface Loaded {
  sources: TrackingSource[]
  providers: TrackingProvider[]
}

/**
 * The tracking providers that post positions for this event, in its settings (docs/features/athletes-and-trackers.md,
 * Host): each connected source's state, the webhook URL to hand its provider and what it last posted, and connecting or
 * disconnecting a provider. LNX is one provider among them. The roster its trackers place is on the event's Athletes view.
 */
export function TrackingProvidersPanel({ eventId }: { eventId: string }) {
  const { toast } = useToast()
  const [data, setData] = useState<Loaded | null>(null)
  const [error, setError] = useState('')
  const [now, setNow] = useState(() => Date.now())
  const [choice, setChoice] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)

  const load = useCallback(async () => {
    try {
      const [sources, providers] = await Promise.all([loadTrackingSources(eventId), loadProviders(eventId)])
      setData({ sources, providers })
      setError('')
    } catch (failure) {
      setError(workspaceError(failure, 'Your tracking couldn’t load. Try again.'))
    } finally {
      setNow(Date.now())
    }
  }, [eventId])

  useRefresh(load)

  if (!data) {
    return error ? (
      <Notice tone="error" alert action={<Button onClick={() => void load()}>Try again</Button>}>
        {error}
      </Notice>
    ) : (
      <ContentSkeleton label="Loading tracking" variant="list" count={2} />
    )
  }

  const { sources, providers } = data
  const names = new Map(providers.map((provider) => [provider.key, provider.name]))
  // A push provider connects in one step; a polled one needs its race id, which only the API takes for now. Each
  // connects once here, so a tracker id always has one source to belong to.
  const connected = new Set(sources.map((source) => source.adapter_key))
  const available = providers.filter((provider) => provider.transport === 'push' && !connected.has(provider.key))
  const chosen = available.find((provider) => provider.key === choice) ?? available[0]

  async function connect(provider: TrackingProvider) {
    setConnecting(true)
    try {
      await connectProvider(eventId, provider, nextSourceKey(provider, sources))
      toast({ title: `${provider.name} connected`, description: `Give ${provider.name} its webhook URL.`, tone: 'success' })
      setChoice(null)
      await load()
    } catch (failure) {
      toast({ title: `${provider.name} not connected`, description: apiErrors(failure, 'Try again.'), tone: 'error' })
    } finally {
      setConnecting(false)
    }
  }

  return (
    <div className="space-y-8">
      {error && (
        <Notice tone="error" alert action={<Button variant="secondary" size="compact" onClick={() => void load()}>Try again</Button>}>
          {error}
        </Notice>
      )}
      <p className="text-sm text-neutral-7">
        Your timing or tracking company posts its trackers’ positions here. Say who carries each tracker on the event’s{' '}
        <Link to={`/events/${eventId}/athletes`} className="underline">
          Athletes
        </Link>{' '}
        view.
      </p>

      {sources.length === 0 ? (
        <EmptyState
          icon={<RadioTower />}
          title="No provider connected"
          description="Connect the provider your race uses. Its trackers place athletes, and cameras without the Stream app, on your event map."
        />
      ) : (
        sources.map((source) => (
          <TrackingSourceRow
            key={source.id}
            eventId={eventId}
            source={source}
            provider={names.get(source.adapter_key) ?? source.adapter_key}
            now={now}
            onChanged={() => void load()}
          />
        ))
      )}

      {chosen && (
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="Add a provider"
            className="min-w-48"
            items={available.map((provider) => ({ value: provider.key, label: provider.name }))}
            value={chosen.key}
            onValueChange={setChoice}
          />
          <Button onClick={() => void connect(chosen)} loading={connecting}>
            Connect {chosen.name}
          </Button>
        </div>
      )}
    </div>
  )
}

interface RowProps {
  eventId: string
  source: TrackingSource
  /** The provider's name, such as LNX. */
  provider: string
  now: number
  onChanged: () => void
}

function TrackingSourceRow({ eventId, source, provider, now, onChanged }: RowProps) {
  const [delivery, setDelivery] = useState<WebhookDelivery | null | undefined>(undefined)
  const [rotateOpen, setRotateOpen] = useState(false)
  const [rotateError, setRotateError] = useState('')
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [disconnectError, setDisconnectError] = useState('')
  const headingId = `tracking-source-${source.id}`

  useEffect(() => {
    if (!source.webhook_url) return
    let cancelled = false
    loadLastDelivery(eventId, source.id).then(
      (latest) => !cancelled && setDelivery(latest),
      () => !cancelled && setDelivery(undefined),
    )
    return () => {
      cancelled = true
    }
  }, [eventId, source.id, source.webhook_url, now])

  const fresh = source.last_fresh_at ? now - Date.parse(source.last_fresh_at) : null
  const state =
    source.status !== 'ready' || fresh === null
      ? { tone: 'quiet' as const, label: `Waiting for ${provider}`, line: 'No positions yet.' }
      : fresh <= QUIET_AFTER_MS
        ? { tone: 'success' as const, label: 'Receiving', line: `Last position ${formatAge(fresh)}.` }
        : { tone: 'warning' as const, label: 'Quiet', line: `Last position ${formatAge(fresh)}.` }

  async function rotate() {
    setRotateError('')
    try {
      await rotateWebhook(eventId, source.id)
    } catch (failure) {
      setRotateError(apiErrors(failure, 'A new URL couldn’t be made. Try again.'))
      throw failure
    }
    onChanged()
  }

  async function disconnect() {
    setDisconnectError('')
    try {
      await disconnectSource(eventId, source.id)
    } catch (failure) {
      setDisconnectError(apiErrors(failure, `${provider} couldn’t be disconnected. Try again.`))
      throw failure
    }
    onChanged()
  }

  return (
    <section className="space-y-3" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 id={headingId} className="text-base font-medium">
          {provider}
        </h3>
        <Badge tone={state.tone}>{state.label}</Badge>
      </div>
      <p className="text-xs text-neutral-7">
        {state.line} {deliveryLine(delivery, now)}
      </p>
      {source.webhook_url && (
        <>
          <CopyField label="Webhook URL" value={source.webhook_url} />
          <p className="text-xs text-neutral-7">Give this to {provider}. Anyone with it can post positions, so share it only with them.</p>
        </>
      )}
      <div className="flex flex-wrap gap-2">
        {source.webhook_url && (
          <Button variant="secondary" size="compact" onClick={() => setRotateOpen(true)}>
            New URL
          </Button>
        )}
        <Button variant="secondary" size="compact" onClick={() => setDisconnectOpen(true)}>
          Disconnect
        </Button>
      </div>
      {source.webhook_url && (
        <ConfirmDialog
          open={rotateOpen}
          onOpenChange={setRotateOpen}
          title="Make a new webhook URL?"
          description={`The current URL stops working at once. ${provider} needs the new one to keep posting.`}
          confirmLabel="Make new URL"
          variant="primary"
          onConfirm={rotate}
          failureMessage={rotateError || undefined}
        />
      )}
      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title={`Disconnect ${provider}?`}
        description={`${source.webhook_url ? 'Its webhook URL stops working and its' : 'Its'} trackers go, so the athletes and cameras they placed leave the map. Your roster stays.`}
        confirmLabel={`Disconnect ${provider}`}
        onConfirm={disconnect}
        failureMessage={disconnectError || undefined}
      />
    </section>
  )
}

function deliveryLine(delivery: WebhookDelivery | null | undefined, now: number): string {
  if (delivery === undefined) return ''
  if (delivery === null) return 'Nothing delivered yet.'
  const age = formatAge(now - Date.parse(delivery.received_at))
  if (delivery.fixes_count === null) return `Last delivery ${age} couldn’t be read${delivery.parse_error ? `: ${delivery.parse_error}` : ''}.`
  return `Last delivery ${age} with ${delivery.fixes_count} ${delivery.fixes_count === 1 ? 'position' : 'positions'}.`
}
